import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Tooltip, message } from "antd";
import { DownloadOutlined, ShareAltOutlined } from "@ant-design/icons";
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    pdf,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import { fetchProposalById } from "../../../state/slices/proposalSlice";

const pick = (live, fallback) =>
    live === undefined || live === null || live === "" ? fallback : live;

const upper = (v) => (typeof v === "string" ? v.toUpperCase() : v);

const formatDate = (v) => {
    if (!v) return undefined;
    const d = dayjs(v);
    return d.isValid() ? d.format("DD/MM/YYYY") : undefined;
};

const formatINR = (v) => {
    if (v === undefined || v === null || v === "") return undefined;
    const n = Number(v);
    return isNaN(n) ? String(v) : n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const formatTenure = (months) => {
    const m = Number(months);
    if (!m || isNaN(m)) return undefined;
    const years = m / 12;
    const pretty = Number.isInteger(years) ? years : years.toFixed(1);
    return `${pretty} ${years === 1 ? "year" : "years"}`;
};

const formatGeo = (lat, long) => {
    const la = Number(lat);
    const lo = Number(long);
    if (!lat || !long || isNaN(la) || isNaN(lo)) return undefined;
    return `${la.toFixed(4)}°, ${lo.toFixed(4)}°`;
};

const LOGO_URL = `${window.location.origin}${process.env.PUBLIC_URL || ""}/logo.png`;
const LOGO_SRC = `${process.env.PUBLIC_URL || ""}/logo.png`;

const toDataUrl = async (url) => {
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const blob = await res.blob();
        if (!blob.type.startsWith("image/")) return null;
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        return null;
    }
};

const saveBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};


const buildSections = (data, rs) => {
    const { customer: c, business: b, socio: s, loan: l, property: p } = data;
    const F = (label, value, span = 1) => ({ label, value, span });

    return [
        {
            title: "A. Customer Details",
            rows: [
                [F("Applicant Full Name", c.fullName), F("Aadhar Linked Mobile No.", c.aadharLinkedMobile), F("Alternate Mobile No.", c.alternateMobile)],
                [F("Email Address", c.email), F("Voter ID (EPIC)", c.voterId), F("PAN Number", c.pan)],
                [F("Date of Birth", c.dob), F("Gender", c.gender), F("State", c.state)],
                [F("District", c.district), F("City/Town/Village", c.city), F("Pin Code", c.pincode)],
                [F("Address Line 1", c.addressLine1, 2), F("Residence Geo-Location", c.residenceGeo)],
            ],
        },
        {
            title: "B. Business Details",
            rows: [
                [F("Occupational Category", b.occupationalCategory), F("Occupation", b.occupation)],
                [F("Business Name", b.businessName), F("Vintage of Business (years)", b.vintage), F("No. of full-time workers", b.workers)],
                [F("Business Proof Document", b.proofDocument), F("Business Geo-Location", b.businessGeo)],
            ],
        },
        {
            title: "C. Socio-Economic Details",
            rows: [[F("Education", s.education), F("Religion", s.religion), F("Caste/Category", s.caste)]],
        },
        {
            title: "D. Loan Requirement Details",
            rows: [
                [
                    F("Credit Bureau Score", l.creditScore),
                    F(`Loan Amount Sought (${rs})`, l.amount),
                    F("Tenure of Loan", l.tenure),
                    F("Purpose of Loan", l.purpose),
                ],
            ],
        },
        {
            title: "E. Property Details",
            rows: [
                [F("Property Type", p.type), F("Property Location", p.location), F("Property Address", p.address, 2)],
                [F("Property Area", p.area), F("Rooms in Property", p.rooms), F("Occupancy Status", p.occupancy), F("Age of Property", p.age)],
                [F("Geo-location of Property", p.geo), F("Pin Code", p.pincode)],
                [F("Boundary – North", p.boundary.north), F("Boundary – South", p.boundary.south)],
                [F("Boundary – East", p.boundary.east), F("Boundary – West", p.boundary.west)],
                [F("Type of Approach Road", p.approachRoad), F("Approach Road can accommodate", p.approachVehicle), F("Classification of Land", p.landClass)],
                [F("Owner(s) of Property", p.owners), F("Relation of Owner with Applicant", p.ownerRelation), F(`Estimated Property Value (${rs})`, p.value)],
            ],
        },
        {
            title: "F. Property Related Documents",
            docs: data.documents,
        },
        {
            title: null,
            rows: [[F("Vendor Code","FBGP00464"), F("Application Reference ID", data.applicationRefId)]],
        },
    ];
};

const buildDocRows = (documents) => [
    [
        { text: "Proof of Ownership", flex: 3, head: true },
        { text: "Available (Y/N)", flex: 1, align: "center", head: true },
        { text: "Document Date", flex: 1, align: "center", head: true },
    ],
    ...documents.map((d) => [
        { text: d.name, flex: 3 },
        { text: d.available, flex: 1, align: "center", bold: true },
        { text: d.date, flex: 1, align: "center", bold: true },
    ]),
];


const INK = "#111111";
const BORDER = "#444444";
const HEAD_BG = "#eeeeee";

const styles = {
    desk: { background: "#e8ecf4", padding: "20px 12px 40px", minHeight: "100vh" },
    toolbar: {
        maxWidth: 900,
        margin: "0 auto 12px",
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
    },
    iconBtn: { borderColor: INK, color: INK, background: "#fff" },
    sheet: {
        maxWidth: 900,
        margin: "0 auto",
        background: "#fff",
        padding: 8,
        border: `2px solid ${INK}`,
        boxShadow: "0 4px 18px rgba(0, 0, 0, 0.18)",
    },
    sheetInner: { border: `1px solid ${INK}`, padding: "20px 22px 16px" },
    header: {
        display: "flex",
        alignItems: "center",
        borderBottom: `2px solid ${INK}`,
        paddingBottom: 12,
        marginBottom: 16,
    },
    logoBox: { width: 130, display: "flex", alignItems: "center" },
    logo: { maxWidth: 130, maxHeight: 54, objectFit: "contain" },
    title: {
        flex: 1,
        textAlign: "center",
        fontSize: 18,
        fontWeight: 700,
        color: INK,
        letterSpacing: 0.3,
    },
    section: { marginBottom: 16 },
    sectionTitle: {
        background: INK,
        color: "#fff",
        fontWeight: 700,
        fontSize: 14,
        padding: "5px 12px",
    },
    grid: { borderTop: `1px solid ${BORDER}`, borderLeft: `1px solid ${BORDER}` },
    row: { display: "flex", flexWrap: "wrap" },
    cell: {
        padding: "8px 12px",
        borderRight: `1px solid ${BORDER}`,
        borderBottom: `1px solid ${BORDER}`,
        fontSize: 13.5,
        lineHeight: 1.5,
        minWidth: 170,
        wordBreak: "break-word",
    },
    label: { color: "#555", fontSize: 12.5 },
    value: { fontWeight: 600, color: "#111" },
    footer: {
        marginTop: 8,
        paddingTop: 8,
        borderTop: `1px solid ${BORDER}`,
        fontSize: 12.5,
        color: "#444",
        textAlign: "right",
    },
};

const mobileStyles = {
    desk: { padding: "10px 6px 30px" },
    sheet: { padding: 4 },
    sheetInner: { padding: "14px 10px 10px" },
    header: { flexDirection: "column", gap: 8, paddingBottom: 10, marginBottom: 12 },
    logoBox: { width: "auto", justifyContent: "center" },
    logo: { maxWidth: 110, maxHeight: 44 },
    title: { fontSize: 14, lineHeight: 1.4, letterSpacing: 0, width: "100%" },
    sectionTitle: { fontSize: 13, padding: "4px 10px" },
    cell: { fontSize: 13, padding: "6px 10px", minWidth: 0 },
    label: { fontSize: 11.5 },
    footer: { fontSize: 11.5 },
};

const useIsMobile = (breakpoint = 640) => {
    const [mobile, setMobile] = useState(
        typeof window !== "undefined" && window.innerWidth <= breakpoint
    );
    useEffect(() => {
        const onResize = () => setMobile(window.innerWidth <= breakpoint);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [breakpoint]);
    return mobile;
};

const Field = ({ label, value, span = 1, mobile }) => (
    <div
        style={{
            ...styles.cell,
            flex: mobile ? "1 1 100%" : `${span} 1 0%`,
            ...(mobile ? mobileStyles.cell : {}),
        }}
    >
        <span
            style={{
                ...styles.label,
                ...(mobile ? mobileStyles.label : {}),
                display: mobile ? "block" : "inline",
            }}
        >
            {mobile ? label : `${label}: `}
        </span>
        <span style={styles.value}>{String(value)}</span>
    </div>
);

const DocTable = ({ documents, mobile }) => (
    <div style={styles.grid}>
        {buildDocRows(documents).map((row, ri) => (
            <div key={ri} style={styles.row}>
                {row.map((cell, ci) => (
                    <div
                        key={ci}
                        style={{
                            ...styles.cell,
                            flex: `${cell.flex} 1 0%`,
                            minWidth: mobile ? 0 : ci === 0 ? 170 : 110,
                            ...(mobile ? { fontSize: 12, padding: "6px 6px" } : {}),
                            textAlign: cell.align || "left",
                            background: cell.head ? HEAD_BG : undefined,
                            fontWeight: cell.head || cell.bold ? 700 : 400,
                        }}
                    >
                        {cell.text}
                    </div>
                ))}
            </div>
        ))}
    </div>
);


const line = { borderStyle: "solid", borderColor: BORDER };

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 30,
        paddingHorizontal: 30,
        paddingBottom: 42,
        fontSize: 8,
        fontFamily: "Helvetica",
        color: "#111",
    },
    frame: {
        position: "absolute",
        top: 14,
        left: 14,
        right: 14,
        bottom: 14,
        borderStyle: "solid",
        borderColor: INK,
        borderWidth: 1.5,
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        borderBottomStyle: "solid",
        borderBottomColor: INK,
        borderBottomWidth: 1.5,
        paddingBottom: 8,
        marginBottom: 10,
    },
    logoBox: { width: 90 },
    logo: { width: 90, height: 36, objectFit: "contain", objectPositionX: 0 },
    title: { flex: 1, textAlign: "center", fontSize: 12, fontFamily: "Helvetica-Bold", color: INK },
    section: { marginBottom: 9 },
    sectionTitle: {
        backgroundColor: INK,
        color: "#ffffff",
        fontFamily: "Helvetica-Bold",
        fontSize: 9.5,
        paddingVertical: 3,
        paddingHorizontal: 7,
    },
    grid: { ...line, borderTopWidth: 1, borderLeftWidth: 1 },
    row: { flexDirection: "row" },
    cell: { ...line, borderRightWidth: 1, borderBottomWidth: 1, paddingVertical: 4, paddingHorizontal: 6 },
    label: { color: "#555555", fontSize: 7.5 },
    value: { fontFamily: "Helvetica-Bold", color: "#111111" },
    footer: {
        ...line,
        borderTopWidth: 1,
        marginTop: 4,
        paddingTop: 5,
        textAlign: "right",
        fontSize: 8,
        color: "#444444",
    },
    pageNo: {
        position: "absolute",
        bottom: 20,
        left: 0,
        right: 0,
        textAlign: "center",
        fontSize: 7,
        color: "#666666",
    },
});

const FusionPdf = ({ sections, logo, generatedOn }) => (
    <Document title="Mortgage Loan Lead from CreditX for Fusion" author="CreditX">
        <Page size="A4" style={pdfStyles.page}>
            <View fixed style={pdfStyles.frame} />

            <View style={pdfStyles.header}>
                <View style={pdfStyles.logoBox}>
                    {logo ? <Image src={logo} style={pdfStyles.logo} /> : null}
                </View>
                <Text style={pdfStyles.title}>MORTGAGE LOAN LEAD FROM CREDITX for FUSION</Text>
                <View style={pdfStyles.logoBox} />
            </View>

            {sections.map((sec, si) => (
                <View key={si} style={pdfStyles.section}>
                    {sec.title ? (
                        <Text style={pdfStyles.sectionTitle} minPresenceAhead={50}>
                            {sec.title}
                        </Text>
                    ) : null}

                    <View style={pdfStyles.grid}>
                        {sec.docs
                            ? buildDocRows(sec.docs).map((row, ri) => (
                                <View key={ri} style={pdfStyles.row} wrap={false}>
                                    {row.map((cell, ci) => (
                                        <View
                                            key={ci}
                                            style={[
                                                pdfStyles.cell,
                                                { flex: cell.flex },
                                                cell.head ? { backgroundColor: HEAD_BG } : {},
                                            ]}
                                        >
                                            <Text
                                                style={{
                                                    textAlign: cell.align || "left",
                                                    fontFamily: cell.head || cell.bold ? "Helvetica-Bold" : "Helvetica",
                                                }}
                                            >
                                                {cell.text}
                                            </Text>
                                        </View>
                                    ))}
                                </View>
                            ))
                            : sec.rows.map((row, ri) => (
                                <View key={ri} style={pdfStyles.row} wrap={false}>
                                    {row.map((f, fi) => (
                                        <View key={fi} style={[pdfStyles.cell, { flex: f.span }]}>
                                            <Text>
                                                <Text style={pdfStyles.label}>{f.label}: </Text>
                                                <Text style={pdfStyles.value}>{String(f.value)}</Text>
                                            </Text>
                                        </View>
                                    ))}
                                </View>
                            ))}
                    </View>
                </View>
            ))}

            <Text style={pdfStyles.footer}>
                Application Generated on - {generatedOn} (system generated)
            </Text>

            <Text
                fixed
                style={pdfStyles.pageNo}
                render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
            />
        </Page>
    </Document>
);


const FusionPage = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [busy, setBusy] = useState(null);
    const isMobile = useIsMobile();
    const mob = (key) => (isMobile ? mobileStyles[key] || {} : {});

    const routeState = location?.state || {};
    const routeProposalId = routeState.id;

    const storeVerify = useSelector(
        (state) => state?.fetchProposal?.proposal?.data?.data
    );

    useEffect(() => {
        if (!storeVerify && routeProposalId) {
            dispatch(fetchProposalById({ id: routeProposalId }));
        }
    }, [storeVerify, routeProposalId, dispatch]);


    const proposal = storeVerify || routeState.proposal || {};
    console.log("DEWNDEIDNDE::", proposal)
    const data = useMemo(() => {
        const applicant = proposal?.applicantId || {};
        const add = applicant?.additionalDetails || {};
        const res = add?.residentialAddress || {};
        const emp = proposal?.employmentDetails?.details || {};
        const loan = proposal?.loanDetails || {};
        const prop = proposal?.additionalLoanDetails?.property || {};
        const biz = proposal?.businessDetails || {};

        const fullName = [
            applicant.firstName,
            applicant.middleName,
            applicant.lastName,
        ]
            .filter(Boolean)
            .join(" ")
            .toUpperCase();

        const residenceGeoLive =
            res.latitude != null && res.longitude != null
                ? formatGeo(res.latitude, res.longitude)
                : undefined;

        const businessGeoLive =
            biz.latitude != null && biz.longitude != null
                ? formatGeo(biz.latitude, biz.longitude)
                : undefined;

        return {
            customer: {
                fullName: pick(fullName || "NA"),
                aadharLinkedMobile: pick(applicant.primaryMobile) || "NA",
                alternateMobile: pick(applicant.alternateMobile) || "NA",
                email: pick(applicant.primaryEmail) || "NA",
                voterId: pick(add.voterId || "NA"),
                pan: pick(applicant.pancardId) || "NA",
                dob: pick(formatDate(applicant.dob)) || "NA",
                gender: pick(
                    upper(applicant.gender?.label || applicant.gender?.value)
                ),
                state: pick(upper(res.state?.name || res.state)) || "NA",
                district: pick(upper(res.city?.name || res.city)) || "NA",
                city: pick(upper(res.area)) || "NA",
                pincode: pick(res.pincode) || "NA",
                addressLine1: pick(res.addressLine1) || "NA",
                residenceGeo: pick(residenceGeoLive) || "NA",
            },

            business: {
                occupationalCategory: pick(
                    upper(add.occupationalCategory?.replace?.(/_/g, " ")) || "NA"
                ),
                occupation: pick(
                    emp.occupation?.label || emp.occupation?.value || "NA"
                ),
                businessName: pick(biz.businessName) || "NA",
                vintage: pick(emp.yrsOfExp) || "NA",
                workers: pick(emp.numberOfFullTimeWorkers) || "NA",
                proofDocument: pick(biz.proofDocument) || "NA",
                businessGeo: pick(businessGeoLive) || "NA",
            },

            socio: {
                education: pick(
                    upper(add.education?.label || add.education?.value) || "NA"
                ),
                religion: pick(upper(add.religion)) || "NA",
                caste: pick(add.caste) || "NA",
            },

            loan: {
                creditScore: pick(proposal.creditBureauScore) || "NA",
                amount: pick(formatINR(loan.amount)) || "NA",
                tenure: pick(formatTenure(loan.tenure)) || "NA",
                purpose: pick(loan.purpose?.label || loan.purpose?.value) || "NA",
            },

            property: {
                type: pick(prop.propertyType
                ) || "NA",
                location: pick(prop.propertyLocation
                ) || "NA",
                address: pick(prop.propertyAddress
                ) || "NA",
                area: pick(prop.propertyArea) || "NA",
                rooms: pick(prop.propertyRooms) || "NA",
                occupancy: pick(prop.occupancyStatus) || "NA",
                age: pick(prop.propertyAge ? `${prop.propertyAge} years` : "NA"),
                geo: pick(
                    prop.latitude != null && prop.longitude != null
                        ? formatGeo(prop.latitude, prop.longitude)
                        : "NA"
                ),
                pincode: pick(prop.propertyPincode) || "NA",
                boundary: {
                    north: pick(prop.boundaryNorth) || "NA",
                    south: pick(prop.boundarySouth) || "NA",
                    east: pick(prop.boundaryEast) || "NA",
                    west: pick(prop.boundaryWest) || "NA",
                },
                approachRoad: pick(prop.approachRoadType) || "NA",
                approachVehicle: pick(prop.approachRoadAccommodates
                ) || "NA",
                landClass: pick(prop.landClassification) || "NA",
                owners: pick(prop.propertyOwners[0]) || "NA",
                ownerRelation: pick(prop.ownerRelation) || "NA",
                value: pick(formatINR(prop.propertyValue)) || "NA",
            },

            documents:
                Array.isArray(prop.documents) && prop.documents.length > 0
                    ? prop.documents.map((d) => ({
                        name: d.name,
                        available: d.available ? "Y" : "N",
                        date: d.available
                            ? formatDate(d.date) || "N/A"
                            : "N/A",
                    }))
                    : [],

            vendorCode: pick(proposal.vendorCode) || "NA",
            applicationRefId: pick(
                proposal.applicationRefId || proposal.applicationId || "NA"
            ),
        };
    }, [proposal]);


    const generatedOn = dayjs().format("DD/MM/YYYY");
    const screenSections = useMemo(() => buildSections(data, "₹"), [data]);
    const pdfSections = useMemo(() => buildSections(data, "Rs."), [data]);

    const fileName = `Fusion_Lead_${data.applicationRefId}.pdf`;

    const buildPdfBlob = async () => {
        const logo = await toDataUrl(LOGO_URL);
        return pdf(
            <FusionPdf sections={pdfSections} logo={logo} generatedOn={generatedOn} />
        ).toBlob();
    };

    const [pdfFile, setPdfFile] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setPdfFile(null);
        buildPdfBlob()
            .then((blob) => {
                if (!cancelled) setPdfFile(new File([blob], fileName, { type: "application/pdf" }));
            })
            .catch((err) => console.error("PDF pre-build failed:", err));
        return () => {
            cancelled = true;
        };
    }, [pdfSections, fileName]);

    const handleDownload = async () => {
        try {
            setBusy("download");
            saveBlob(pdfFile || (await buildPdfBlob()), fileName);
        } catch (err) {
            console.error("PDF download failed:", err);
            message.error("Could not create the PDF");
        } finally {
            setBusy(null);
        }
    };

    const handleShare = async () => {
        const shareText =
            `Mortgage Loan Lead from CreditX for Fusion\n` +
            `Applicant: ${data.customer.fullName}\n` +
            `Loan Amount: Rs. ${data.loan.amount}\n` +
            `Reference ID: ${data.applicationRefId}`;
        const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

        const fallback = async () => {
            window.open(waUrl, "_blank", "noopener,noreferrer");
            saveBlob(pdfFile || (await buildPdfBlob()), fileName);
            message.info("PDF downloaded. Attach it in the WhatsApp chat.");
        };

        try {
            setBusy("share");

            const canShareFile =
                pdfFile &&
                typeof navigator.share === "function" &&
                typeof navigator.canShare === "function" &&
                navigator.canShare({ files: [pdfFile] });

            if (canShareFile) {
                try {
                    await navigator.share({ files: [pdfFile], title: "Fusion lead form", text: shareText });
                } catch (err) {
                    if (err?.name === "AbortError") return;
                    await fallback();
                }
            } else {
                await fallback();
            }
        } catch (err) {
            console.error("Share failed:", err);
            message.error("Could not share the form");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div style={{ ...styles.desk, ...mob("desk") }}>
            <div style={styles.toolbar}>
                <Tooltip title="Download PDF">
                    <Button
                        shape="circle"
                        size="large"
                        style={styles.iconBtn}
                        icon={<DownloadOutlined />}
                        loading={busy === "download"}
                        disabled={busy === "share"}
                        onClick={handleDownload}
                    />
                </Tooltip>
                <Tooltip title="Share on WhatsApp">
                    <Button
                        shape="circle"
                        size="large"
                        style={styles.iconBtn}
                        icon={<ShareAltOutlined />}
                        loading={busy === "share"}
                        disabled={busy === "download"}
                        onClick={handleShare}
                    />
                </Tooltip>
            </div>

            <div style={{ ...styles.sheet, ...mob("sheet") }}>
                <div style={{ ...styles.sheetInner, ...mob("sheetInner") }}>
                    <div style={{ ...styles.header, ...mob("header") }}>
                        <div style={{ ...styles.logoBox, ...mob("logoBox") }}>
                            <img
                                src={LOGO_SRC}
                                alt="CreditX"
                                style={{ ...styles.logo, ...mob("logo") }}
                                onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                }}
                            />
                        </div>
                        <div style={{ ...styles.title, ...mob("title") }}>
                            {isMobile ? (
                                <>
                                    MORTGAGE LOAN LEAD
                                    <br />
                                    FROM CREDITX for FUSION
                                </>
                            ) : (
                                "MORTGAGE LOAN LEAD FROM CREDITX for FUSION"
                            )}
                        </div>
                        {!isMobile && <div style={styles.logoBox} />}
                    </div>

                    {screenSections.map((sec, si) => (
                        <div key={si} style={styles.section}>
                            {sec.title ? <div style={{ ...styles.sectionTitle, ...mob("sectionTitle") }}>{sec.title}</div> : null}

                            {sec.docs ? (
                                <DocTable documents={sec.docs} mobile={isMobile} />
                            ) : (
                                <div style={styles.grid}>
                                    {sec.rows.map((row, ri) => (
                                        <div key={ri} style={styles.row}>
                                            {row.map((f, fi) => (
                                                <Field key={fi} {...f} mobile={isMobile} />
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    <div style={{ ...styles.footer, ...mob("footer") }}>
                        Application Generated on - {generatedOn} (system generated)
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FusionPage;